/**
 * InstitorAI DevHub: l'app desktop per Windows, compresa nel piano Premium o con la licenza
 * singola (ADR 0065, ADR 0066). Rotte del servizio `billing`:
 *
 * - `GET /api/v1/billing/devhub/versions`: versioni pubblicate, dalla più recente;
 * - `POST /api/v1/billing/devhub/download`: link firmato (10 minuti) della versione chiesta,
 *   la più recente senza corpo;
 * - `GET /api/v1/billing/devhub/license`: la chiave di licenza (token `DHL1.…`), rinnovata
 *   se l'abbonamento è stato rinnovato; 404 `devhub_license_missing` se non è mai stata
 *   emessa; `POST` la emette.
 *
 * Senza diritto 403 `devhub_not_entitled`. Con 404, 501 o 503 (servizio non configurato o
 * nessuna versione) la pagina mostra «in preparazione» invece di un errore (`isNotReady`).
 */

import { ApiError, type ApiClient } from './client';

/** Prefisso delle rotte di DevHub. */
const DEVHUB = '/api/v1/billing/devhub';

/** Una versione pubblicata dell'app. */
export interface DevhubVersion {
  /** Numero di versione, per esempio `0.3.0`. */
  version: string;
  /** Data di pubblicazione, ISO 8601. */
  published_at: string;
  /** Note di rilascio, testo breve. */
  notes: string;
  /** Impronta SHA-256 del file, in esadecimale. */
  sha256: string;
  /** Dimensione del file in byte, se nota. */
  size_bytes?: number | null;
}

/** Link di download firmato. */
export interface DevhubDownload {
  /** Indirizzo da aprire: scade in pochi minuti. */
  url: string;
  /** Versione scaricata. */
  version: string;
  /** Impronta SHA-256 del file. */
  sha256: string;
  /** Scadenza del link, ISO 8601. */
  expires_at: string;
}

/** La licenza dell'utente. */
export interface DevhubLicense {
  /** Id della licenza, per la revoca. */
  lic_id: string;
  /** Chiave da incollare nell'app (`DHL1.…`). */
  token: string;
  /** Piano della licenza (`pro` per il Premium). */
  plan: string;
  /** Scadenza, ISO 8601: la fine del periodo pagato. */
  expires_at: string;
  /** Data di emissione, ISO 8601. */
  issued_at: string;
}

/**
 * True se l'errore dice che la parte del servizio non c'è ancora (rotta assente o spenta).
 *
 * @param caught Errore della chiamata.
 * @returns True per 404, 501 e 503.
 */
export function isNotReady(caught: unknown): boolean {
  return caught instanceof ApiError && [404, 501, 503].includes(caught.status);
}

/** Codice del 403: serve il Premium attivo. */
export const DEVHUB_REQUIRES_PREMIUM = 'devhub_requires_premium';

/** Chiamate di InstitorAI DevHub. */
export class DevhubApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Versioni pubblicate.
   *
   * @returns Le versioni, dalla più recente.
   */
  async versions(): Promise<DevhubVersion[]> {
    const body = await this.client.request<{ items: DevhubVersion[] }>('GET', `${DEVHUB}/versions`);
    return body.items;
  }

  /**
   * Link firmato per scaricare una versione.
   *
   * @param version Versione; senza, la più recente.
   * @returns Il link, che scade in pochi minuti.
   */
  download(version?: string): Promise<DevhubDownload> {
    return this.client.request<DevhubDownload>('POST', `${DEVHUB}/download`, version === undefined ? {} : { version });
  }

  /**
   * Licenza dell'utente.
   *
   * @returns La licenza; `null` se non è ancora stata emessa.
   */
  async license(): Promise<DevhubLicense | null> {
    try {
      return await this.client.request<DevhubLicense>('GET', `${DEVHUB}/license`);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404 && caught.code === 'devhub_license_missing') {
        return null;
      }
      throw caught;
    }
  }

  /**
   * Emette la licenza dell'utente (o la rinnova alla fine del periodo pagato).
   *
   * @returns La licenza.
   */
  issueLicense(): Promise<DevhubLicense> {
    return this.client.request<DevhubLicense>('POST', `${DEVHUB}/license`, {});
  }
}
